<template>
  <div class="d-flex flex-column align-center justify-center">
    <Logo />
  </div>
  <Card v-if="currentStep < 4">
    <div class="d-flex flex-column align-center justify-center w-100 ga-3">
      <h1 class="text-main-color">Pension Keeper</h1>
      <div class="text-title-color font-weight-600">Quick Check</div>
      <p class="font-size-12">Answer a couple of questions so we can locate your application.</p>
      <div class="step-container">
        <div class="step-container__name">{{ stepTitle }}</div>
        <div class="step-container__number">Step {{ currentStep }} of 4</div>
      </div>
      <!--Step 1-->
      <div v-if="currentStep === 1" class="d-flex flex-column align-center justify-center w-100 ga-3">
        <div class="text-title-color font-weight-700 font-size-16">Are you an XtraPension client?</div>
        <div class="custom-radio-container d-flex flex-column align-center justify-center w-100 ga-3">
          <div
            v-for="option in areYouXtraPensionClientOptions"
            :key="option.label"
            class="custom-radio-container__item"
            @click="handleChangeXtraPensionClient(option.value)"
          >
            <div class="radio">
              <div v-if="option.value === areYouXtraPensionClient" class="checked"></div>
            </div>
            <div>{{ option.label }}</div>
          </div>
        </div>
        <div class="sign-up-container" v-if="areYouXtraPensionClient === false">
          <button class="main-btn" @click="goToStep(4)">Join Pension Keeper now</button>
          <!-- <p class="font-size-14"><b>Not a client yet? Sign up now.</b></p> -->
        </div>
        <div class="d-flex align-center justify-end w-100" v-if="areYouXtraPensionClient">
          <button class="main-btn" @click="goToStep(2)" :disabled="areYouXtraPensionClient == null">Continue</button>
        </div>
      </div>
      <!--Step 2-->
      <div v-if="currentStep === 2" class="d-flex flex-column align-center justify-center w-100 ga-3">
        <p class="font-size-12">Enter your details exactly as used on your application.</p>
        <VForm v-model="formValid" @submit.prevent>
          <VRow class="mt-2">
            <VCol cols="12">
              <div class="d-flex flex-column gap-6">
                <label class="text-title-color font-weight-700">What's your application number?</label>
                <VTextField
                  label=""
                  variant="outlined"
                  color="primary"
                  name="applicatioNumber"
                  type="text"
                  placeholder="e.g. A05000"
                  v-model="findApplicationForm.applicationNumber"
                  :disabled="isLoadingFindApplication"
                  :rules="formRules.applicationNumber"
                  class="custom-input"
                />
                <!-- <div class="input-helptext">Allowed format: A05000 to A09999 and A10000 onwards.</div> -->
              </div>
            </VCol>
            <VCol cols="12">
              <div class="d-flex flex-column gap-6">
                <label class="text-title-color font-weight-700">Which email did you give us previously?</label>
                <VTextField
                  label=""
                  variant="outlined"
                  color="primary"
                  name="email"
                  type="email"
                  placeholder="e.g. name@example.com"
                  v-model="findApplicationForm.email"
                  :disabled="isLoadingFindApplication"
                  :rules="formRules.email"
                  class="custom-input"
                />
              </div>
            </VCol>
          </VRow>
          <div v-if="isAppicationNotFound" class="info-container info-container__red mt-3">
            <template v-if="appicationNotFoundErrorMessage">
              <p>{{ appicationNotFoundErrorMessage }}</p>
              <div
                v-if="
                  appicationNotFoundErrorMessage.includes(
                    'XtraPension client with this application number has already signed up.'
                  )
                "
                class="d-flex align-center justify-center mt-4"
              >
                <button class="white-btn" @click="navigateTo('Login')">Login Now</button>
              </div>
            </template>
            <template v-else>
              <p class="font-weight-600">We couldn’t find a match.</p>
              <p>Please double-check the application number and email, then try again.</p>
            </template>
          </div>
          <div v-if="isLoadingFindApplication" class="d-flex flex-column align-center justify-center py-4 w-100">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <div class="d-flex align-center justify-center w-100 gap-10 mt-3">
            <button class="white-btn" @click="goToStep(1)" :disabled="isLoadingFindApplication">Back</button>
            <button
              class="main-btn"
              type="submit"
              @click="handleFindApplication()"
              :disabled="isLoadingFindApplication"
            >
              Find Me
            </button>
          </div>
        </VForm>
      </div>
      <!--Step 3-->
      <div v-if="currentStep === 3" class="d-flex flex-column align-center justify-center w-100 ga-3">
        <p class="font-size-12">We found an application that matches your details.</p>
        <VRow class="mt-2">
          <VCol cols="12" sm="6">
            <div class="review-input">
              <div class="review-input__label">Application No</div>
              <div class="review-input__value">{{ foundApplication.applicationNumber }}</div>
            </div>
          </VCol>
          <VCol cols="12" sm="6">
            <div class="review-input">
              <div class="review-input__label">Name</div>
              <div class="review-input__value">{{ fountApplicationFullName }}</div>
            </div>
          </VCol>
          <template v-if="isShowPayNow">
            <VCol cols="12" sm="6">
              <div class="review-input">
                <div class="review-input__label">Email</div>
                <div class="review-input__value">{{ maskEmail(foundApplication.email) }}</div>
              </div>
            </VCol>
            <VCol cols="12" sm="6">
              <div class="review-input">
                <div class="review-input__label">DOB</div>
                <div class="review-input__value">{{ maskDOB(foundApplication.dob) }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">National Insurance Number</div>
                <div class="review-input__value">{{ maskNINO(foundApplication.nationalInsuranceNumber) }}</div>
              </div>
            </VCol>
            <VCol cols="12">
              <div class="review-input">
                <div class="review-input__label">Current Address</div>
                <div class="review-input__value">{{ maskAddress(fountApplicationFullAddress) }}</div>
              </div>
            </VCol>
          </template>
          <VCol cols="12">
            <div class="d-flex align-center w-100 ga-3">
              <div class="text-title-color">*My partner is a XtraPension client?</div>
              <div class="custom-radio-container d-flex align-center justify-center ga-3">
                <div class="custom-radio-container__item" @click="handleChangeParner(true)">
                  <div class="radio">
                    <div v-if="foundApplication.isPartnerXPClient" class="checked"></div>
                  </div>
                  <div>Yes</div>
                </div>
                <div class="custom-radio-container__item" @click="handleChangeParner(false)">
                  <div class="radio">
                    <div v-if="!foundApplication.isPartnerXPClient" class="checked"></div>
                  </div>
                  <div>No</div>
                </div>
              </div>
            </div>
          </VCol>
          <VCol cols="12">
            <div class="registration-free-note">
              *Spouse registration is FREE. Please allow us 5 working days to setup their free account.
            </div>
          </VCol>
          <VCol v-if="isShowPayNow" cols="12">
            <div v-if="isPhonePage" class="mt-3 mb-2">
              <VSelect
                label="Which Agent Did You Speak to?"
                :items="agentsOptions"
                v-model="agent"
                variant="outlined"
                :disabled="isLoadingPayNow"
                class="custom-input"
              >
              </VSelect>
            </div>
            <div class="hr"></div>
            <div class="review-input mt-3">
              <div class="credit-card">
                <div class="d-flex gap-16">
                  <VIcon icon="mdi-credit-card" size="small"></VIcon>
                  <div>{{ 'XXXX XXXX XXXX ' + foundApplication.cardData.last4Digits }}</div>
                </div>
                <div>{{ foundApplication.cardData.expireMonth + '/' + foundApplication.cardData.expireYear }}</div>
              </div>
            </div>
            <div class="text-main-color my-2">
              <i
                ><b>This service renews automatically every year.</b> You'll continue to be billed until you cancel. You
                can stop your subscription at any time from your account billing settings.</i
              >
            </div>
          </VCol>
        </VRow>
        <div class="d-flex align-center justify-center w-100 gap-10">
          <button v-if="isShowPayNow" class="main-btn" @click="handlePayNow()" :disabled="isLoadingPayNow">
            Pay Now {{ currencySymbol }}{{ formatAmount(amount) }}
          </button>
          <button class="main-btn" @click="goToStep(4)">Update your Payment Method</button>
        </div>
        <div class="input-helptext text-center">Click Pay Now Using previous CC details we've on file</div>
      </div>
    </div>
  </Card>
  <div v-else-if="currentStep === 4" class="payment-form-container">
    <PaymentForm :foundApplication="foundApplication?.applicationId ? foundApplication : {}" />
  </div>
  <Card v-else>
    <ThankYou>
      <template v-slot:title>
        <span class="text-main-color">Thanks!</span> We’re preparing your file <br />
        and will share your login details soon.
      </template>
      <template v-slot:text>
        You should get an email within the next hour with your <br />
        Pension Keeper file. <i>(Check your spam folder too)</i>.
      </template>
    </ThankYou>
  </Card>
  <div class="step-container__footer">
    <a href="https://www.xtrapension.com/annual" target="_blank">
      Learn More About This <br />
      Annual Support Service
    </a>
    <router-link :to="{ name: 'Login' }">Already Paid? <br />LOGIN here</router-link>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
// Service
import applicationsService from '@/services/applications-service/applications.service';
import StripeService from '@/services/stripe-service/stripe.service';
// Utils
import { formatFoundApplication } from '@/utils/formatData';
// Components
import Card from '../Card/Card.vue';
import Logo from '../Logo/Logo.vue';
import PaymentForm from '../PaymentForm/PaymentForm.vue';
import ThankYou from '../ThankYou/ThankYou.vue';
export default {
  name: 'SaleStepComponent',
  inject: ['agentsOptions'],
  components: {
    Card,
    Logo,
    PaymentForm,
    ThankYou
  },
  computed: {
    isShowPayNow() {
      return (
        this.foundApplication?.customerId &&
        this.foundApplication?.paymentMethod &&
        this.foundApplication?.cardData?.last4Digits &&
        this.foundApplication?.cardData?.expireMonth &&
        this.foundApplication?.cardData?.expireYear &&
        this.foundApplication?.isShowApplicationDetails
      );
    },
    stepTitle() {
      if (this.currentStep === 1) {
        return 'First';
      } else if (this.currentStep === 2) {
        return 'Client verification';
      } else if (this.currentStep === 3) {
        return 'Application found';
      } else {
        return '';
      }
    },
    fountApplicationFullName() {
      return this.foundApplication.firstName + ' ' + this.foundApplication.lastName;
    },
    fountApplicationFullAddress() {
      const addressParts = [
        this.foundApplication.currentAddress?.street,
        this.foundApplication.currentAddress?.city,
        this.foundApplication.currentAddress?.state,
        this.foundApplication.currentAddress?.postalCode,
        this.foundApplication.currentAddress?.country
      ].filter((part) => part && part.trim() !== '');

      return addressParts.length > 0 ? addressParts.join(', ') : 'Address not provided';
    },
    currencyCode() {
      let currency = 'USD';
      if (this.isEuropeanCountry(this.foundApplication.currentAddress.countryCode)) {
        currency = 'EUR';
      } else if (this.foundApplication.currentAddress.countryCode === 'CA') {
        currency = 'CAD';
      } else if (this.foundApplication.currentAddress.countryCode === 'US') {
        currency = 'USD';
      } else if (this.foundApplication.currentAddress.countryCode === 'AU') {
        currency = 'AUD';
      } else if (this.foundApplication.currentAddress.countryCode === 'NZ') {
        currency = 'NZD';
      } else if (this.foundApplication.currentAddress.countryCode === 'ZA') {
        currency = 'ZAR';
      }
      return currency;
    },
    currencySymbol() {
      let currencySymbol = 'US $';
      if (this.isEuropeanCountry(this.foundApplication.currentAddress.countryCode)) {
        currencySymbol = '€';
      } else if (this.foundApplication.currentAddress.countryCode === 'CA') {
        currencySymbol = 'CA $';
      } else if (this.foundApplication.currentAddress.countryCode === 'AU') {
        currencySymbol = 'AU $';
      } else if (this.foundApplication.currentAddress.countryCode === 'NZ') {
        currencySymbol = 'NZ $';
      } else if (this.foundApplication.currentAddress.countryCode === 'ZA') {
        currencySymbol = 'R';
      }
      return currencySymbol;
    },
    amount() {
      let amount = 15000;
      if (this.isEuropeanCountry(this.foundApplication.currentAddress.countryCode)) {
        amount = 12500;
      } else if (
        this.foundApplication.currentAddress.countryCode === 'AU' ||
        this.foundApplication.currentAddress.countryCode === 'NZ' ||
        this.foundApplication.currentAddress.countryCode === 'CA'
      ) {
        amount = 20000;
      } else if (this.foundApplication.currentAddress.countryCode === 'ZA') {
        amount = 200000;
      }
      return amount;
    }
  },
  data() {
    return {
      currentStep: 1,
      // Step 1
      areYouXtraPensionClient: null,
      areYouXtraPensionClientOptions: [
        {
          label: 'Yes',
          value: true
        },
        {
          label: 'No',
          value: false
        }
      ],
      // Step 2
      formRules: {
        applicationNumber: [(v) => !!v || 'Application Number required'],
        email: [(v) => !!v || 'Email required', (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid']
      },
      formValid: false,
      findApplicationForm: {
        applicationNumber: '',
        email: ''
      },
      isLoadingFindApplication: false,
      isAppicationNotFound: false,
      // Step 3
      foundApplication: {
        applicationId: '',
        applicationNumber: '',
        firstName: '',
        lastName: '',
        dob: '',
        email: '',
        nationalInsuranceNumber: '',
        currentAddress: {
          street: '',
          state: '',
          postalCode: '',
          country: '',
          city: ''
        },
        cardData: {
          cardBrand: '',
          expireMonth: 0,
          expireYear: '',
          last4Digits: ''
        },
        paymentMethod: '',
        customerId: '',
        isPartnerXPClient: false,
        isShowApplicationDetails: false
      },
      isLoadingPayNow: false,
      isAppCreateError: false,
      customer: '',
      latest_charge: '',
      leadId: this.$route.query.leadId ?? null,
      agent: '',
      isPhonePage: false
    };
  },
  mounted() {
    if (this.$route.name === 'Phone Page') {
      this.agent = 'Mike';
      this.isPhonePage = true;
    }
    if (this.$route.query.applicationNumber && this.$route.query.email) {
      this.currentStep = 2;
      this.findApplicationForm = {
        applicationNumber: this.$route.query.applicationNumber,
        email: this.$route.query.email
      };
      this.handleFindApplication(true);
    }
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    handleChangeXtraPensionClient(value) {
      this.areYouXtraPensionClient = value;
    },
    handleChangeParner(value) {
      this.foundApplication.isPartnerXPClient = value;
    },
    async handleFindApplication(isUpdatePaymentMethod = false) {
      this.isAppicationNotFound = false;
      this.appicationNotFoundErrorMessage = '';
      if (this.formValid || isUpdatePaymentMethod) {
        this.isLoadingFindApplication = true;
        try {
          const { foundApplication } = await applicationsService.findApplication(this.findApplicationForm);
          this.foundApplication = formatFoundApplication(foundApplication);
          if (isUpdatePaymentMethod) {
            this.goToStep(4);
          } else {
            this.goToStep(3);
          }
        } catch (err) {
          this.isAppicationNotFound = true;
          console.error(err);
          if (err?.message) {
            this.appicationNotFoundErrorMessage = err.message;
          }
          this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
        } finally {
          this.isLoadingFindApplication = false;
        }
      }
    },
    goToStep(step) {
      this.currentStep = step;
    },
    handlePayNow() {
      this.stripePaymentMethodHandler();
    },
    formatAmount(amount) {
      return amount / 100;
    },
    async stripePaymentMethodHandler() {
      this.isLoadingPayNow = true;
      try {
        const res = await StripeService.sendFormData({
          payment_method_id: this.foundApplication.paymentMethod,
          customerId: this.foundApplication.customerId,
          currency: this.currencyCode,
          amount: this.amount,
          email: this.foundApplication.email,
          setup_future_usage: false,
          shiping_details: {
            name: this.fountApplicationFullName,
            phone: this.foundApplication.phone,
            address: {
              line1: this.foundApplication?.currentAddress?.street ?? '',
              city: this.foundApplication?.currentAddress?.city ?? '',
              state: this.foundApplication?.currentAddress?.state ?? '',
              country: this.foundApplication.currentAddress?.countryCode ?? '',
              postal_code: this.foundApplication.currentAddress?.postalCode ?? ''
            }
          },
          isMoto: this.isPhonePage,
          agent: this.agent,
          leadId: this.leadId
        });
        const paymentResponse = res;
        this.handleServerResponse(paymentResponse);
      } catch (err) {
        this.isLoadingPayNow = false;
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async handleServerResponse(response) {
      try {
        const { error, requires_action, payment_intent_client_secret } = response;
        if (error) {
          this.showSnackbar({ text: this.stripeValidationError, color: 'red' });
        } else if (requires_action) {
          const { error: errorAction, paymentIntent } = await this.stripe.handleCardAction(
            payment_intent_client_secret
          );
          if (errorAction) {
            // this.stripeValidationError = response.error.message;
          } else {
            const serverResponse = await await StripeService.sendFormData({
              payment_intent_id: paymentIntent.id
            });
            this.handleServerResponse(await serverResponse);
          }
        } else {
          const { id, status, paymentMethod, customer, latest_charge } = response;
          // console.log(id, status, paymentMethod, customer, latest_charge);
          this.customer = customer;
          this.latest_charge = latest_charge;
          if (status === 'Success' && id && paymentMethod) {
            this.goToStep(5);
            this.updateApplication(id, paymentMethod, customer);
          }
        }
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoadingPayNow = false;
      }
    },
    async updateApplication(id, paymentMethod, customer) {
      this.isLoadingPayNow = true;
      try {
        const { status } = await applicationsService.createPkPayment({
          id: id,
          applicationId: this.foundApplication.applicationId,
          amount: this.amount,
          currencyCode: this.currencyCode,
          email: this.foundApplication.email,
          name: this.fountApplicationFullName,
          firstName: this.foundApplication.firstName,
          lastName: this.foundApplication.lastName,
          phone: this.foundApplication.phone,
          address: {
            street: this.foundApplication?.currentAddress?.street ?? '',
            city: this.foundApplication?.currentAddress?.city ?? '',
            state: this.foundApplication?.currentAddress?.state ?? '',
            country: this.foundApplication.currentAddress?.countryCode ?? '',
            postalCode: this.foundApplication.currentAddress?.postalCode ?? ''
          },
          paymentMethod: paymentMethod,
          customer: customer ?? null,
          latest_charge: this.latest_charge,
          isPartnerXPClient: this.foundApplication.isPartnerXPClient,
          agent: this.agent
        });
        if (status === 'success') {
          this.isAppCreateError = false;
        } else {
          this.isAppCreateError = true;
        }
      } catch (err) {
        console.error(err);
        this.isAppCreateError = true;
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoadingPayNow = false;
      }
    },
    isEuropeanCountry(countryCode) {
      const europeanCountryCodes = [
        'AL',
        'AD',
        'AM',
        'AT',
        'AZ',
        'BY',
        'BE',
        'BA',
        'BG',
        'CH',
        'CY',
        'CZ',
        'DE',
        'DK',
        'EE',
        'ES',
        'FI',
        'FR',
        'GE',
        'GR',
        'HR',
        'HU',
        'IE',
        'IS',
        'IT',
        'KZ',
        'LI',
        'LT',
        'LU',
        'LV',
        'MC',
        'MD',
        'ME',
        'MK',
        'MT',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'RU',
        'RS',
        'SE',
        'SI',
        'SK',
        'SM',
        'TR',
        'UA',
        'UK',
        'VA',
        'GI',
        'JE',
        'GG'
      ];
      return europeanCountryCodes.includes(countryCode?.toUpperCase());
    },
    // Email: show first 2 chars and domain, mask the rest
    maskEmail(email) {
      if (!email) return '';
      const [localPart, domain] = email.split('@');
      if (localPart.length <= 2) {
        return email; // Return as-is if too short
      }
      const visible = localPart.substring(0, 2);
      const masked = '*'.repeat(localPart.length - 2);
      return `${visible}${masked}@${domain}`;
    },

    // DOB: show only year, hide day and month
    maskDOB(dob) {
      if (!dob) return '-';
      // Assuming dob is in DD/MM/YYYY or YYYY-MM-DD format
      // Try to extract year
      const parts = dob.split(/[/-]/);
      let year;
      if (parts.length === 3) {
        // Check if first part is year (YYYY-MM-DD) or last part is year (DD/MM/YYYY)
        if (parts[0].length === 4) {
          year = parts[0];
        } else if (parts[2].length === 4) {
          year = parts[2];
        } else {
          return dob; // Can't determine format
        }
      } else {
        return dob;
      }
      return `** / ** / ${year}`;
    },

    // NINO: show first 4 chars, mask the rest
    maskNINO(nino) {
      if (!nino) return '-';
      if (nino.length <= 4) return nino;
      const visible = nino.substring(0, 4);
      const masked = '*'.repeat(nino.length - 4);
      return `${visible}${masked}`;
    },

    // Address: show first 4 characters/digits, mask the rest
    maskAddress(address) {
      if (!address) return '-';
      if (address.length <= 4) return address;
      const visible = address.substring(0, 4);
      const masked = address.substring(4).replace(/\S/g, '*');
      return `${visible}${masked}`;
    },
    navigateTo(pageName) {
      if (!pageName) {
        return;
      }
      this.$router.push({ name: pageName });
    }
  }
};
</script>
